import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaGem, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import "../i18n"; // Import i18n setup

const AboutCompany = () => {
  const { t } = useTranslation(['common', 'aboutCompany']);
  const [currentImage, setCurrentImage] = useState(1);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const images = [
    "/images/others/1.svg",
    "/images/others/2.svg",
    "/images/others/3.svg",
    "/images/others/4.svg",
    "/images/others/5.svg",
    "/images/others/6.svg",
    "/images/others/7.svg",
    "/images/others/8.svg",
    "/images/others/9.svg"
  ];

  const nextSlide = useCallback(() => {
    setCurrentImage((prev) => (prev % images.length) + 1);
  }, [images.length]);

  const prevSlide = () => {
    setCurrentImage((prev) => (prev === 1 ? images.length : prev - 1));
  };

  // Auto-advance slideshow
  useEffect(() => {
    let timer;
    if (isAutoPlaying) {
      timer = setInterval(nextSlide, 3000);
    }
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section id="about-company" className="py-4 sm:py-4 md:py-16 lg:py-16 px-2">
      <div className="container mx-auto flex flex-col lg:flex-row items-center lg:items-start px-6 lg:px-20">
        {/* Left Column */}
        <div className="relative w-full lg:w-1/2 flex items-end justify-center p-6 lg:p-12 rounded-br-[100px] overflow-hidden min-h-[333px] sm:min-h-[500px] md:min-h-[500px]">
          {/* Background Images */}
          <div className="absolute inset-0 z-0">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Office ${index + 1}`}
                className={`w-full h-full object-contain absolute transition-opacity duration-1000 ${currentImage === index + 1 ? 'opacity-100' : 'opacity-0'
                  }`}
              />
            ))}
          </div>

          {/* Navigation Controls - Responsive positioning */}
          <div className="absolute bottom-4 md:bottom-6 lg:bottom-8 left-0 right-0 flex justify-center items-center gap-2 md:gap-4 z-10">
            <button
              onClick={prevSlide}
              className="p-1.5 md:p-2 bg-black/50 hover:bg-black/75 rounded-full transition-colors"
              aria-label="Previous image"
            >
              <FaChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-1.5 md:gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index + 1)}
                  className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-colors ${currentImage === index + 1 ? 'bg-orange-400' : 'bg-gray-400'
                    }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-1.5 md:p-2 bg-black/50 hover:bg-black/75 rounded-full transition-colors"
              aria-label="Next image"
            >
              <FaChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className={`p-1.5 md:p-2 rounded-full transition-colors text-xs md:text-sm ${isAutoPlaying ? 'bg-orange-400' : 'bg-gray-400'
                }`}
              aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
            >
              {isAutoPlaying ? '⏸' : '▶'}
            </button>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-1/2 space-y-6 md:space-y-8 mt-6 sm:pl-6">
          {/* Heading */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-center lg:text-left">
            {t("aboutCompany:aboutCompany.title")}
          </h1>

          {/* Paragraph */}
          <p className="text-lg max-w-2xl mb-4 text-left md:text-justify lg:text-justify">
            {t("aboutCompany:aboutCompany.description")}
          </p>

          <hr className="border-gray-700" />

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 gap-6 md:gap-8">
            {/* Mission */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-start">
              <div className="bg-orange-400 text-black p-3 md:p-4 rounded-full shrink-0">
                <FaBuilding className="w-5 h-5 md:w-7 md:h-7" />
              </div>
              <div className="text-center md:text-left flex flex-col w-full">
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  <a href="/about/corporatephilosophy#mission" className="hover:text-orange-400 transition-colors">
                    {t("aboutCompany:mission.title")}
                  </a>
                </h3>
                <p className="text-gray-300 leading-relaxed text-left md:text-justify lg:text-justify">
                  {t("aboutCompany:mission.description")}
                </p>
                <div className="flex justify-end mt-4">
                  <a
                    href="/about/corporatephilosophy#mission"
                    className="text-sm text-orange-400 hover:underline"
                  >
                    {t("common:seeDetails")}
                  </a>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-start">
              <div className="bg-orange-400 text-black p-3 md:p-4 rounded-full shrink-0">
                <FaGem className="w-5 h-5 md:w-7 md:h-7" />
              </div>
              <div className="text-center md:text-left flex flex-col w-full">
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  <a href="/about/corporatephilosophy#vision" className="hover:text-orange-400 transition-colors">
                    {t("aboutCompany:vision.title")}
                  </a>
                </h3>
                <p className="text-gray-300 leading-relaxed text-left md:text-justify lg:text-justify">
                  {t("aboutCompany:vision.description")}
                </p>
                <div className="flex justify-end mt-4">
                  <a
                    href="/about/corporatephilosophy#vision"
                    className="text-sm text-orange-400 hover:underline"
                  >
                    {t("common:seeDetails")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutCompany;